<template lang="pug">
  div(:class="[{ check: check || editIsVisible, editIsVisible: editIsVisible, error, ['hideError']: error && noErrorMessage }, 'label-position-' + labelPosition, $style.txt]")

    .label(
      v-if="!['animated', 'border'].includes(labelPosition) && label"
      :class="[classes]")
      label.text(:for="txtId") {{ label }}

    .text-input
      .relative
        input(
          ref="input"
          :id="txtId"
          :type="type"
          :value="modelValue"
          :placeholder="placeholderText"
          :class="['placeholder-gray-600 p-ds-md border border-mono-200 rounded-md', classes.join(' '), customClasses, {active: activeFlag.value, showArrows}]"
          @input="$emit('update:modelValue', $event.target.value, $event)"
          @blur="onBlur"
          @focus="onFocus"
          @change="$emit('change', $event.target.value)"
          :required="labelPosition == 'animated'"
          step="any"
          :disabled="disabled || editIsVisible"
          :autocomplete="autocomplete"
          :style="styles.join(';')"
          :min="min"
          :max="max")

        div(
          v-if="iconLeft"
          class="left-0 ml-ds-xxxs"
          :class="$style.placeholderIcon")
          Icon(
            :color="iconColor"
            :name="iconLeft"
            :size="iconFontSize"
            :style="iconStyles.join(';')")

        div(
          v-if="iconRight && !editIsVisible && !check"
          class="right-0 mr-ds-xxxs"
          :class="$style.placeholderIcon")
          Icon(
            :color="iconColor"
            :name="iconRight"
            :size="iconFontSize"
            :style="iconStyles.join(';')")

        .check(
          v-show="check || editIsVisible"
          :class="[{ 'checkWithEdit': editIsVisible }, $style.placeholderIcon]")
          Icon(
            name="i:check-solid"
            :size="14")

        .edit(
          v-if="editIsVisible"
          :class="$style.placeholderIcon")
          a(@click="editIsClicked = true") Edit

        .label(
          v-if="['animated', 'border'].includes(labelPosition) && label"
          :class="[classes, labelBg ? 'bg-' + labelBg : 'label-bg-default']")
          label(:for="txtId") {{ label }}

      .msg.flex(v-if="error && !noErrorMessage")
        Icon(
          name="i:triangle-exclamation-solid"
          :size="16")
        .text {{ error }}

</template>

<script setup lang="ts">
import { getSizeClasses } from "~/scripts/css-helper"
import useUniqueId from "~/use/unique-id"

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },
  /** Add a green check icon */
  check: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ""
  },
  labelPosition: {
    type: String,
    default: "top",
    validator: value => ["top", "left", "animated", "border"].includes(value)
  },
  labelBg: {
    type: String,
    default: ""
  },
  error: {
    type: String,
    default: ""
  },
  /** Hide error message - just red placeholder and border */
  noErrorMessage: {
    type: Boolean,
    default: false
  },
  /** Standard input types */
  type: {
    type: String,
    default: "text"
  },
  size: {
    type: String,
    default: "l",
    validator: value => ["xs", "s", "m", "l", "xl"].includes(value)
  },
  inputId: {
    type: String
  },
  placeholder: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  checkAndDisable: {
    type: Boolean,
    default: false
  },
  focused: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: "off",
    required: false
  },
  /** Icon shortcut */
  iconLeft: {
    type: String,
    default: ""
  },
  /** Icon shortcut */
  iconRight: {
    type: String,
    default: ""
  },
  /** Icon size */
  // eslint-disable-next-line vue/require-default-prop
  iconSize: {
    type: Number,
    required: false
  },
  /** Icon colour */
  // eslint-disable-next-line vue/require-default-prop
  iconColor: {
    type: String,
    required: false
  },
  min: {
    type: Number,
    required: false,
    default: null
  },
  max: {
    type: Number,
    required: false,
    default: null
  },
  showArrows: {
    type: Boolean,
    default: false
  },
  border: {
    type: Boolean,
    default: true
  },
  backgroundColor: {
    type: String,
    default: ""
  },
  iconPadding: {
    type: Number,
    required: false,
    default: null
  },
  iconPaddingEnabled: {
    type: Boolean,
    default: false
  },
  customClasses: {
    type: String
  }
})

const input = ref(null)
const activeFlag = ref(false)
const editIsClicked = ref(false)
const xPadding = ref(null)

const emit = defineEmits(["update:modelValue", "change", "focus", "blur", "input-loaded"])

defineExpose({
  input
})

const placeholderText = computed(() => {
  return props.labelPosition !== "animated" ? props.placeholder : ""
})
const classes = computed(() => getSizeClasses(props.size))
const editIsVisible = computed(() => props.checkAndDisable && !editIsClicked.value)
const iconFontSize = computed(() => {
  let size = 32

  if (props.iconSize) {
    size = props.iconSize
  } else {
    switch (size) {
      case "xs":
        size = 16
        break
      case "s":
        size = 18
        break
      case "m":
        size = 20
        break
      case "l":
        size = 24
        break
      case "xl":
        size = 32
        break
    }
  }

  return size
})
const iconStyles = computed(() => {
  const styles = []

  if (props.iconPaddingEnabled) {
    if (props.iconLeft || props.iconRight) {
      styles.push(`width: ${xPadding.value}px`)
    }
  }

  if (props.iconColor) {
    styles.push(`color: ${props.iconColor}`)
  }

  return styles
})

const styles = computed(() => {
  const styles = []

  if (props.iconPaddingEnabled) {
    if (props.iconLeft) {
      styles.push(`padding-left: ${xPadding.value}px`)
    }

    if (props.iconRight) {
      styles.push(`padding-right: ${xPadding.value}px`)
    }
  }

  if (!props.border) {
    styles.push(`border: 0`)
  }

  if (props.backgroundColor) {
    styles.push(`background-color: ${props.backgroundColor}`)
  }

  return styles
})

const txtId = computed(() =>
  props.inputId ? props.inputId : "txt-" + useId().replace(/[^a-zA-Z0-9]/g, "_")
)

onMounted(() => {
  if (props.focused) {
    focus()
  }

  if (props.iconLeft || props.iconRight) {
    if (props.iconPadding) {
      xPadding.value = props.iconPadding
    } else {
      xPadding.value = props.iconSize ? props.iconSize * 2 : iconFontSize.value * 2
    }
  }

  emit("input-loaded")
})

function focus() {
  input.value.focus()
}

function onFocus() {
  activeFlag.value = true

  if (props.autocomplete === "off" && input.value) {
    input.value.setAttribute("autocomplete", "chrome-off")
  }

  emit("focus")
}

function onBlur() {
  activeFlag.value = false

  if (props.autocomplete === "off" && input.value) {
    input.value.setAttribute("autocomplete", "off")
  }

  emit("blur")
}
</script>

<style lang="scss" module>
:global {
  :local(.txt) {
    position: relative;

    input {
      @apply text-gray-800 transition-all;
      background-color: transparent;
      opacity: 1; /* required on iOS */
      width: 100%;

      &:focus:not([readonly]) {
        @apply border-blue-primary;
      }

      &.xs-size {
        padding: 8px;
      }

      &[readonly],
      &[disabled] {
        cursor: not-allowed;
      }

      &:not(.showArrows) {
        &[type="number"]::-webkit-inner-spin-button,
        &[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          margin: 0;
        }

        &[type="number"] {
          -moz-appearance: textfield;
        }
      }
    }

    &.error {
      input {
        @apply border-red-primary;
        border-bottom-color: transparent;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    &.hideError {
      input {
        @apply border-red-primary rounded text-red-primary;

        &::placeholder {
          @apply text-red-primary;
        }
      }
    }

    &.label-position-left {
      @apply flex text-base;
    }

    input:focus ~ .msg {
      box-shadow: 0 0 0 2px lighten(#c7c7c7, 10%);
    }

    .text-input {
      position: relative;
    }

    .label {
      @apply text-base;
      display: table;
      padding-right: 1em;

      .text {
        vertical-align: middle;
        display: table-cell;
      }
    }

    .msg {
      @apply font-latoMedium border border-solid border-red-primary;
      padding: 7.5px 15px;
      border-bottom-left-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
      @apply text-red-primary;
      background-color: #f7e9eb;

      .icon {
        flex: none;
        margin-top: 2px;
      }

      .text {
        @apply text-base;
        display: inline-block;
        font-size:16px;
        position: relative;
        top: -1px;
        font-weight: bold;
        margin-left: 5px;
        word-break: break-word;
      }
    }

    &.check {
      input {
        padding-right: 36px;
      }
      .check {
        right: 14px;
        @apply text-green-primary;
      }
    }

    &.editIsVisible {
      input {
        padding-right: 68px;
      }

      .check.checkWithEdit {
        right: 50px;
      }

      .edit {
        right: 14px;
      }
    }

    &.label-position-animated,
    &.label-position-border {
      .label {
        color: #a4a4a4;
        position: absolute;
        left: 16px;
        top: 13px;
        transition: all 0.2s ease-in;
        z-index: 10;
        padding: 0;
        height: auto;
        pointer-events: none;
      }

      label {
        cursor: text;
      }

      input:focus ~ .label,
      input:valid ~ .label,
      input:disabled ~ .label,
      input[readonly] ~ .label {
        @apply text-sm text-base-dark -top-ds-ms;
        padding: 2px 4px;
        height: 20px;
      }

      .label-bg-default {
        background: rgb(255, 255, 255);
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0) 40%,
          rgba(255, 255, 255, 1) 45%,
          rgba(255, 255, 255, 1) 100%
        );
      }

      // Need to do autofill separately because Firefox can't handle it
      input:-webkit-autofill ~ .label {
        @apply text-base -top-ds-md;
        left: 26px;
        padding: 2px 4px;
        background-color: #fff;
        height: 20px;
      }

      &.hideError {
        .label {
          @apply text-red-primary;
        }
      }
    }
  }
}

.placeholderIcon {
  @apply absolute h-full top-0 flex items-center justify-center;
  width: 50px;
}

@screen md {
  .label:global(.label-position-border) {
    background: #fff;
  }
}
</style>
